import axiosConfig from "./axiosConfig";

// Placing a drafted outreach email into the sending account's Gmail drafts folder,
// and the one-time OAuth connect that makes it possible. Nothing here sends: the
// send stays a human action in Gmail.

// Whether this environment can place drafts, and whether an account is connected.
// `configured` false means the OAuth client / secret is not wired up here at all, so
// the settings card explains that instead of offering a Connect button that would
// fail.
export interface OutreachGmailStatus {
  configured: boolean;
  // When `configured` is false, what is actually missing, in operator language -
  // so standing an environment up doesn't mean guessing which half is absent.
  // Optional so an older backend degrades to the bare "not configured" line.
  configurationGaps?: string[];
  connected: boolean;
  googleEmail: string | null;
  status: string;
  authorizedBy: string | null;
  lastError: string | null;
  connectedAt: string | null;
}

export interface OutreachGmailConnectResponse {
  authUrl: string;
  state: string;
}

// The outcomes the operator has to decide about come back as a `status` in a 200
// rather than an error, so the modal branches on them:
// - "placed": in the drafts folder, ids recorded.
// - "draft_edited": the sender changed it by hand - subject, body or recipients.
//   `liveSubject` / `liveBody` / `liveTo` / `liveCc` / `liveBcc` are what they would
//   lose (the recipients are raw header values, possibly empty); calling again with
//   `confirmProceed` replaces it anyway.
// - "draft_vanished": the draft has left the folder, so it was either sent or
//   discarded - which is deliberately not knowable, because finding out would mean
//   holding read access to the sender's whole inbox. Calling again with
//   `confirmProceed` places a new one.
// - "not_connected": no Google account connected on this environment yet.
// - "already_sent": this contact has already been emailed, so nothing was placed.
//   `message` says so in operator language.
export interface OutreachGmailDraftResponse {
  status:
    | "placed"
    | "draft_edited"
    | "draft_vanished"
    | "not_connected"
    | "already_sent";
  gmailDraftId?: string;
  gmailThreadId?: string | null;
  toEmail?: string;
  subject?: string;
  liveSubject?: string;
  liveBody?: string;
  liveTo?: string;
  liveCc?: string;
  liveBcc?: string;
  message?: string;
}

// `warning` is set when Google's revocation could not be confirmed: the stored
// credential is gone either way, but the grant may still be live at Google until
// someone removes it by hand.
export interface OutreachGmailDisconnectResponse {
  status: string;
  googleEmail: string | null;
  revoked: boolean;
  warning: string | null;
}

export interface OutreachGmailDraftRequest {
  // Both default to the thread's latest saved turn, so the caller only sends them
  // when the operator has adjusted the text on screen.
  subject?: string;
  body?: string;
  toEmail?: string;
  // Answers whichever guard tripped: "yes, replace the edited draft" or "yes,
  // place a new one even though the last is no longer in the folder".
  confirmProceed?: boolean;
}

export interface OutreachRefreshSentResponse {
  checked: number;
  sent: number;
  // Drafts that have left the folder. Deliberately not "discarded": they were
  // either sent or discarded and the backend does not claim to know which.
  vanished: number;
}

export const getOutreachGmailStatus = async (
  authToken: string
): Promise<OutreachGmailStatus> => {
  const { data } = await axiosConfig.get("/leads/outreach/gmail-status", {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export const postOutreachGmailConnect = async (
  authToken: string,
  body: { redirectUri: string; loginHint?: string | null }
): Promise<OutreachGmailConnectResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/outreach/gmail-connect",
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postOutreachGmailCallback = async (
  authToken: string,
  body: {
    code: string;
    state: string;
    redirectUri: string;
    userEmail?: string | null;
  }
): Promise<{ googleEmail: string; status: string }> => {
  const { data } = await axiosConfig.post(
    "/leads/outreach/gmail-callback",
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postOutreachGmailDisconnect = async (
  authToken: string
): Promise<OutreachGmailDisconnectResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/outreach/gmail-disconnect",
    {},
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postOutreachGmailDraft = async (
  authToken: string,
  leadId: string,
  outreachMessageId: string,
  body: OutreachGmailDraftRequest
): Promise<OutreachGmailDraftResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/${leadId}/outreach/${outreachMessageId}/gmail-draft`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postOutreachRefreshSent = async (
  authToken: string,
  leadIds: string[]
): Promise<OutreachRefreshSentResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/outreach/refresh-sent",
    { leadIds },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};
